import React from 'react';
import { Menu, MenuItem } from 'react-bootstrap-typeahead';
import { css } from 'emotion';
import {
  BuildingIcon,
  LocationIcon,
  TextButton,
  SpinningLoader,
} from '@weave/design-system';
import { weaveTheme } from '@weave/theme-original/dist/weaveTheme';

import { LocationSearchModel } from '../../../../models/location-search.model';
import { OrgSearchModel } from '../../../../models/org-search.model';

const sectionHeader = css`
  color: ${weaveTheme.colors.gray500};
  font-size: 12px;
  font-weight: 600 !important;
  padding: 12px 12px 6px;
  margin: 0;
  text-transform: none;
`;

const emptySection = css`
  color: ${weaveTheme.colors.gray500};
  font-size: 13px;
  padding: 8px 12px;
`;

const resultBadge = (kind: 'location' | 'org') => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  flex-shrink: 0;
  color: ${kind === 'org' ? weaveTheme.colors.weaveBlue : weaveTheme.colors.success};
`;

const resultRow = css`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const resultLabel = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 400;
  color: ${weaveTheme.colors.gray600};
`;

const menuItemClass = (isFirst: boolean, isLast: boolean) => css`
  margin: 0;

  & > a {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    background-color: ${weaveTheme.colors.white};
    border: none;
    color: ${weaveTheme.colors.gray600};
    font-size: 13px;
  }

  &.active > a,
  & > a:hover {
    background-color: ${weaveTheme.colors.gray100};
    color: ${weaveTheme.colors.gray600};
  }
`;

export enum SearchScopes {
  All = 'all',
  Organization = 'organization',
  Location = 'location',
  IdSlug = 'idSlug',
}

export type CombinedSearchResult =
  | ({ kind: 'location' } & LocationSearchModel)
  | ({ kind: 'org' } & OrgSearchModel);

interface GroupedSearchMenuProps {
  menuProps: any;
  results: CombinedSearchResult[];
  searchQuery: string;
  searchScope: SearchScopes;
  isLoading?: boolean;
}

export const SearchResultRow = ({ result }: { result: CombinedSearchResult }) => {
  return (
    <div className={resultRow}>
      <span className={resultBadge(result.kind)}>
        {result.kind === 'org' ? <BuildingIcon size={14} /> : <LocationIcon size={14} />}
      </span>
      <span className={resultLabel}>{result.Name}</span>
    </div>
  );
};

export const GroupedSearchMenu = ({
  menuProps,
  results,
  searchQuery,
  searchScope,
  isLoading,
}: GroupedSearchMenuProps) => {
  if (isLoading) {
    return (
      <Menu {...menuProps}>
        <li className={emptySection} role="option" aria-disabled="true">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SpinningLoader size="small" />
            <span style={{ marginLeft: '8px', color: weaveTheme.colors.gray600 }}>
              Searching...
            </span>
          </div>
        </li>
      </Menu>
    );
  }

  const organizationResults = results.filter((result) => result.kind === 'org');
  const locationResults = results.filter((result) => result.kind === 'location');

  const renderSection = (
    label: string,
    kind: 'org' | 'location',
    sectionResults: CombinedSearchResult[]
  ) => (
    <React.Fragment key={kind}>
      <li className={`dropdown-header ${sectionHeader}`}>
        {label} · {kind === 'org' ? organizationResults.length : locationResults.length}
      </li>
      {sectionResults.length ? (
        sectionResults.map((result, index) => {
          const isFirst = index === 0;
          const isLast = index === sectionResults.length - 1;

          return (
            <MenuItem
              key={result.kind === 'org' ? result.OrgId : result.LocationID}
              option={result}
              position={results.indexOf(result)}
              className={menuItemClass(isFirst, isLast)}
            >
              <SearchResultRow result={result} />
            </MenuItem>
          );
        })
      ) : (
        <li className={emptySection} role="option" aria-disabled="true">
          No {label.toLowerCase()} match '{searchQuery}'
        </li>
      )}
    </React.Fragment>
  );

  return (
    <Menu {...menuProps}>
      {searchScope !== SearchScopes.Location &&
        renderSection('Organizations', 'org', organizationResults)}
      {searchScope !== SearchScopes.Organization &&
        renderSection('Locations', 'location', locationResults)}
    </Menu>
  );
};
